import { generateMutations } from '@homeservices/tool-kit/utils/store';
import getRiskAssessmentConfig from '@homeservices/api-kit/workorder/riskassessment/getRiskAssessmentConfig';

const initialState = () => ({
  riskAssessmentConfig: null,
  isLoadingRiskAssessmentConfig: false,
  riskAssessmentError: null,
});

function getRiskAssessmentConfigState (state) {
  return (state.riskAssessmentConfig && state.riskAssessmentConfig.data)
    || state.riskAssessmentConfig
    || {};
}

function toBoolean (value) {
  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'string') {
    return value.trim().toLowerCase() === 'true';
  }

  if (typeof value === 'number') {
    return value === 1;
  }

  return false;
}

export default function riskAssessment () {
  return {
    namespaced: true,
    state: initialState(),

    getters: {
      isFeatureEnabled: (state) => {
        const config = getRiskAssessmentConfigState(state);
        const ras = config && config.ras;
        return toBoolean(ras && ras.feat_enabled);
      },
      pilottedSteps: (state) => {
        const config = getRiskAssessmentConfigState(state);
        const ras = config && config.ras;
        return (ras && ras.steps) || [];
      },
      formSteps: (state) => {
        const config = getRiskAssessmentConfigState(state);
        const ras = config && config.ras;
        return ras && ras.form_steps;
      },

      // RCP (Recaptcha) configuration
      rcpScore: (state) => {
        const config = getRiskAssessmentConfigState(state);
        const ras = config && config.ras;
        const rcp = ras && ras.rcp;
        return Array.isArray(rcp) ? rcp.includes('scoring') : false;
      },
      rcpDecision: (state) => {
        const config = getRiskAssessmentConfigState(state);
        const ras = config && config.ras;
        const rcp = ras && ras.rcp;
        return Array.isArray(rcp) ? rcp.includes('decision') : false;
      },

      // TMX configuration
      tmxConfig: (state) => {
        const config = getRiskAssessmentConfigState(state);
        const ras = config && config.ras;
        const tmx = ras && ras.tmx;
        return Array.isArray(tmx) ? tmx : [];
      },

      // SVC configuration
      svcConfig: (state) => {
        const config = getRiskAssessmentConfigState(state);
        const ras = config && config.ras;
        return (ras && ras.svc) || null;
      },
    },

    actions: {
      async fetchRiskAssessmentConfig ({ commit, dispatch }, params = {}) {
        commit('updateIsLoadingRiskAssessmentConfig', true);
        commit('updateRiskAssessmentError', null);
        try {
          const { data } = await getRiskAssessmentConfig(params);
          const riskAssessmentConfig = (data && data.data) || data || {};
          if (riskAssessmentConfig.ras) {
            const featEnabled = riskAssessmentConfig.ras.feat_enabled;
            riskAssessmentConfig.ras.feat_enabled = toBoolean(featEnabled);
          }
          commit('updateRiskAssessmentConfig', riskAssessmentConfig);
          // Inject TMX script if TMX is enabled
          // Only inject if TMX array has items
          if (
            riskAssessmentConfig.ras
            && Array.isArray(riskAssessmentConfig.ras.tmx)
            && riskAssessmentConfig.ras.tmx.length > 0
          ) {
            dispatch('injectTMXScript', { appName: params.app_name });
          }
        } catch (e) {
          commit('updateRiskAssessmentError', 'Failed to fetch Risk Assessment Config');
          throw new Error(`Failed to fetch Risk Assessment Config: ${e.message}`);
        } finally {
          commit('updateIsLoadingRiskAssessmentConfig', false);
        }
      },

      injectTMXScript ({ rootGetters }) {
        const org_id = window.__SERVER_CTX__.globals.TMX_ORG_ID;
        const session_id = rootGetters['recaptcha/userInteractionId'];
        const page_id = '10';
        // Check if ThreatMetrix script is already loaded
        if (document.querySelector('script[src*="h.online-metrix.net/fp/tags.js"]')) {
          return;
        }

        // Create and inject ThreatMetrix script tag
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.src = `https://h.online-metrix.net/fp/tags.js?org_id=${org_id}&session_id=${session_id}&pageid=${page_id}`;
        document.head.appendChild(script);

        // Add noscript fallback iframe
        const noscript = document.createElement('noscript');
        const iframe = document.createElement('iframe');
        iframe.style.cssText = 'width: 100px; height: 100px; border: 0; position: absolute; top: -5000px;';
        iframe.src = `https://h.online-metrix.net/fp/tags?org_id=${org_id}&session_id=${session_id}&pageid=${page_id}`;
        noscript.appendChild(iframe);
        document.body.appendChild(noscript);
      },
    },

    mutations: {
      ...generateMutations([
        'riskAssessmentConfig',
        'isLoadingRiskAssessmentConfig',
        'riskAssessmentError',
      ]),
    },
  };
}
